import { useScienceTutorSection, tutorSelection } from './scienceTutorContext';
import { ArrowRight, FlaskConical, RotateCcw } from 'lucide-react';
import { useState } from 'react';

import { CaseList, FeedbackPanel, ValueBar } from './FuelsSustainabilityShared';
import { formatSig, type AnswerCheck } from './gasLaws';
import { ORGANIC_TRANSFORMATIONS } from './organicChemistry';
import { SynthesisViewToggle, SynthesisYieldExplore, type SynthesisView } from './SynthesisExploreViews';
import {
  PATHWAY_CASES,
  checkOverallYield,
  checkPathwayStep,
  cumulativeYields,
  findTransformation,
  moleculeAfter,
  pathwayFormula,
  pathwayTarget,
  pathwayTitle,
  yieldWorking,
  type PathwayCase,
  type PathwayMolecule,
  type StepCheck,
} from './synthesisDesign';

function MoleculeCard({ item, role }: { item: PathwayMolecule; role: string }) {
  return (
    <div className="min-w-0 rounded-2xl border border-hairline bg-surface px-3 py-2">
      <p className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-slate2">{role}</p>
      <p className="text-sm font-extrabold text-ink">{item.name}</p>
      <p className="font-mono text-xs font-bold text-slate2">{pathwayFormula(item)}</p>
    </div>
  );
}

function RouteProgress({ entry, completed }: { entry: PathwayCase; completed: number }) {
  const remaining = entry.steps.length - completed;
  return (
    <ol className="flex flex-wrap items-center gap-2" aria-label="Route so far">
      <li>
        <MoleculeCard item={entry.start} role="Start" />
      </li>
      {entry.steps.slice(0, completed).map((item, index) => (
        <li key={`${item.transformationId}-${index}`} className="flex items-center gap-2">
          <span className="flex flex-col items-center text-[11px] font-bold text-slate2">
            <ArrowRight className="h-4 w-4 text-brand-sky" aria-hidden="true" />
            {findTransformation(item.transformationId)?.label}
          </span>
          <MoleculeCard item={item.product} role={`After step ${index + 1}`} />
        </li>
      ))}
      {remaining > 0 ? (
        <li className="flex items-center gap-2 text-sm font-extrabold text-slate2">
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
          {remaining} more step{remaining === 1 ? '' : 's'} to {pathwayTarget(entry).name}
        </li>
      ) : null}
    </ol>
  );
}

function YieldPanel({ entry }: { entry: PathwayCase }) {
  const [answer, setAnswer] = useState('');
  useScienceTutorSection('YieldPanel', { answer });
  const [check, setCheck] = useState<AnswerCheck | null>(null);
  const cumulative = cumulativeYields(entry);
  const revealed = check !== null && check.status !== 'invalid';

  return (
    <div className="mt-6 grid gap-4 rounded-2xl border border-brand-sky/30 bg-wash-sky p-4">
      <p className="text-sm font-extrabold text-ink">Route complete. Now predict the overall yield.</p>
      <table className="w-full text-left text-sm" aria-label="Step yields">
        <thead>
          <tr className="text-xs font-extrabold uppercase tracking-[0.1em] text-slate2">
            <th className="py-1 pr-2">Step</th>
            <th className="py-1 pr-2">Reaction</th>
            <th className="py-1">Yield</th>
          </tr>
        </thead>
        <tbody>
          {entry.steps.map((item, index) => (
            <tr key={`${item.transformationId}-${index}`} className="border-t border-hairline/70 font-bold text-ink">
              <td className="py-1 pr-2">{index + 1}</td>
              <td className="py-1 pr-2">{findTransformation(item.transformationId)?.label}</td>
              <td className="py-1 tabular-nums">{item.yieldPercent}%</td>
            </tr>
          ))}
        </tbody>
      </table>
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          const cleaned = answer.trim().replace('%', '');
          setCheck(checkOverallYield(entry, cleaned === '' ? Number.NaN : Number(cleaned)));
        }}
      >
        <label className="grid gap-1 text-sm font-extrabold text-ink">
          Overall yield (%)
          <input
            value={answer}
            onChange={(event) => {
              setAnswer(event.target.value);
              setCheck(null);
            }}
            inputMode="decimal"
            aria-label="Overall yield in percent"
            className="w-36 rounded-2xl border border-hairline bg-canvas-pure px-3 py-2 text-sm font-bold text-ink"
          />
        </label>
        <button type="submit" className="btn-pill-primary">
          Check yield
        </button>
      </form>
      {check ? (
        <FeedbackPanel good={check.status === 'correct'} title={check.title} label="Yield feedback">
          <p>{check.feedback}</p>
        </FeedbackPanel>
      ) : null}
      {revealed ? (
        <figure className="min-w-0 rounded-2xl border border-hairline bg-canvas-pure p-4">
          <figcaption className="text-sm font-extrabold text-ink">Product left after each step</figcaption>
          <p className="text-xs font-medium text-slate2">{yieldWorking(entry)}</p>
          <div className="mt-3 grid gap-1.5">
            <ValueBar label="Start" value={100} max={100} valueText="100%" highlight={false} tone="sky" />
            {cumulative.map((value, index) => (
              <ValueBar
                key={`${entry.id}-${index}`}
                label={`After step ${index + 1}`}
                value={value}
                max={100}
                valueText={`${formatSig(value, 3)}%`}
                highlight={index === cumulative.length - 1}
                tone="mint"
              />
            ))}
          </div>
        </figure>
      ) : null}
    </div>
  );
}

export function SynthesisDesignPathwayMode() {
  const [selected, setSelected] = useState<PathwayCase>(PATHWAY_CASES[0]);
  useScienceTutorSection('SynthesisDesignPathwayMode', { selected: tutorSelection(selected) });
  const [completed, setCompleted] = useState(0);
  const [lastCheck, setLastCheck] = useState<StepCheck | null>(null);
  const [view, setView] = useState<SynthesisView>('explore');
  const current = moleculeAfter(selected, completed);
  const finished = completed >= selected.steps.length;

  const choose = (id: string) => {
    const next = PATHWAY_CASES.find((entry) => entry.id === id);
    if (!next) return;
    setSelected(next);
    setCompleted(0);
    setLastCheck(null);
  };

  const tryReaction = (transformationId: string) => {
    const check = checkPathwayStep(selected, completed, transformationId);
    setLastCheck(check);
    if (check.verdict === 'advance') setCompleted((value) => value + 1);
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
      <CaseList
        heading="Choose a target"
        label="Synthesis targets"
        items={PATHWAY_CASES.map((entry) => ({
          id: entry.id,
          label: pathwayTitle(entry),
          detail: `${entry.steps.length} step${entry.steps.length === 1 ? '' : 's'}`,
        }))}
        selectedId={selected.id}
        onSelect={choose}
      />

      <section className="min-w-0 rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
        <div className="mb-4">
          <SynthesisViewToggle view={view} onChange={setView} />
        </div>
        {view === 'explore' ? (
          <>
            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
              <FlaskConical className="h-4 w-4" aria-hidden="true" /> Explore yields
            </p>
            <h3 className="mt-1 text-xl font-extrabold text-ink">How much {pathwayTarget(selected).name} do you get?</h3>
            <SynthesisYieldExplore key={selected.id} entry={selected} />
          </>
        ) : (
          <>
            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
              <FlaskConical className="h-4 w-4" aria-hidden="true" /> Plan the route
            </p>
            <h3 className="mt-1 text-xl font-extrabold text-ink">Make {pathwayTarget(selected).name}</h3>
            <p className="mt-1 text-sm font-medium leading-6 text-slate2">
              You hold <strong className="text-ink">{current.name}</strong>. Choose the next reaction.
            </p>

            <div className="mt-4">
              <RouteProgress entry={selected} completed={completed} />
            </div>

            {!finished ? (
              <div className="mt-5 grid gap-2 sm:grid-cols-2" role="group" aria-label="Reactions">
                {ORGANIC_TRANSFORMATIONS.map((transformation) => (
                  <button
                    key={transformation.id}
                    type="button"
                    onClick={() => tryReaction(transformation.id)}
                    className="rounded-2xl border border-hairline bg-surface px-4 py-2.5 text-left text-sm font-extrabold text-ink hover:border-ink/40"
                  >
                    <span className="block">{transformation.label}</span>
                    <span className="mt-0.5 block text-xs font-bold text-slate2">
                      {transformation.reagent}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}

            {lastCheck ? (
              <div className="mt-4">
                <FeedbackPanel
                  good={lastCheck.verdict === 'advance'}
                  title={lastCheck.verdict === 'advance' ? 'Good step.' : 'That step does not work here.'}
                  label="Step feedback"
                >
                  <p>{lastCheck.feedback}</p>
                </FeedbackPanel>
              </div>
            ) : null}

            {finished ? (
              <>
                <p className="mt-4 text-sm font-medium leading-6 text-slate2">{selected.note}</p>
                <YieldPanel key={selected.id} entry={selected} />
          </>
        ) : null}

        <button
          type="button"
          onClick={() => {
            setCompleted(0);
            setLastCheck(null);
          }}
          className="mt-5 inline-flex items-center gap-2 rounded-full border border-hairline px-4 py-2 text-sm font-extrabold text-ink hover:bg-surface"
        >
          <RotateCcw className="h-4 w-4" aria-hidden="true" /> Reset route
        </button>
          </>
        )}
      </section>
    </div>
  );
}
